/**
 * Cube Client Factory
 *
 * Creates and configures a Cube.js client instance that connects
 * to the backend's embedded Cube API endpoint.
 *
 * - WebSocket transport against /cubejs-api/v1 (one socket for the whole app)
 * - Authentication rides the session cookie sent on the upgrade handshake
 */

import cubejs, { CubeApi } from '@cubejs-client/core';
import WebSocketTransport from '@cubejs-client/ws-transport';

export type CubeClientConfig = {
  /** Base URL of the backend server (e.g. http://localhost:3000) */
  baseUrl: string;
  /** Optional auth token to pass as Authorization header */
  authToken?: string;
};

/**
 * Get the Cube REST API URL from a backend base URL
 */
export const getCubeApiUrl = ( baseUrl: string ): string => {
  const cleanBase = baseUrl.replace( /\/$/, '' );
  return `${cleanBase}/cubejs-api/v1`;
};

/**
 * The same endpoint as a WebSocket URL.
 *
 * `http:` -> `ws:`, `https:` -> `wss:`. A RELATIVE base (the default when
 * `VITE_BACKEND_URL` is unset — the dev server proxies `/cubejs-api`) has no scheme
 * to swap, so it is resolved against the page's own origin first; a bare
 * `ws://cubejs-api/v1` would name a host called "cubejs-api".
 */
export const getCubeWsUrl = ( baseUrl: string ): string => {
  const apiUrl = getCubeApiUrl( baseUrl );
  const absolute = new URL( apiUrl, window.location.origin );
  absolute.protocol = absolute.protocol === 'https:' ? 'wss:' : 'ws:';
  return absolute.toString();
};

/**
 * Create a Cube.js client instance configured for the backend.
 *
 * ## Why WebSocket, and why it works now (2026-09-02)
 *
 * Every widget used to issue its OWN `cubeApi.load()` — N widgets, N HTTP round
 * trips, and no refresh of any kind once they resolved: the dashboard was a
 * snapshot taken at mount. `subscribe()` over one socket replaces both problems at
 * once. Each widget still gets its own message (the grain the UI wants — a slow
 * tile never blocks a fast one), but they share a single connection, and the server
 * PUSHES a new result when the underlying data moves.
 *
 * This file used to say the WS transport was unusable:
 *
 *   > The Cube WebSocket subscription server returns a `load` envelope whose
 *   > per-result `query` field is absent, so `@cubejs-client/core`'s
 *   > `loadResponseInternal` crashes on `response.results[0].query.responseFormat`.
 *
 * The observation was real and the diagnosis was wrong. The envelope was malformed
 * because OUR `initSubscriptionServer` callback did a bare `JSON.stringify` of a
 * native `ResultWrapper` instead of awaiting `getFinalResult()` the way Cube's own
 * HTTP responder does. That is fixed in `setupCube.ts` (`serializeWsFrame`), and
 * the envelope has been correct ever since — so the transport is no longer routed
 * around.
 *
 * ## Authentication
 *
 * There is no bearer token on this path and `authorization` below is NOT a
 * credential — the server OVERWRITES it with the connection id before Cube reads
 * it. The real identity comes from the session cookie the browser sends on the
 * WebSocket upgrade, which the server resolves once per connection
 * (`cubeWsIdentity.ts`). It must still be a STRING: `ws-transport` sends
 * `{ authorization: this.authorization }` as the handshake frame, and `undefined`
 * would `JSON.stringify` to `{}`, which Cube rejects as a malformed message rather
 * than an unauthenticated one.
 *
 * ⚠️ Why the cookie rides a CROSS-ORIGIN upgrade (app.teselly.com -> api.teselly.com),
 * given that it is `SameSite=Lax` and has no `Domain=` attribute
 * (`identity/authentication.system.ts` mints
 * `<name>=<token>; HttpOnly; Secure; SameSite=Lax; Max-Age=…; Path=/`):
 *
 *  - **Same-SITE, not same-origin.** `SameSite` is evaluated on the registrable
 *    domain, and both hosts are under `teselly.com`, so a request from the app
 *    origin to the API origin is same-site and `Lax` does not withhold the cookie.
 *    Cross-ORIGIN is what forced `credentials: 'include'` on the old HTTP transport;
 *    it is a different axis and does not apply to a WebSocket handshake, which has
 *    no such opt-in and always sends what the cookie rules allow.
 *  - **Host-only is fine because the socket targets the ISSUING host.** With no
 *    `Domain=`, the cookie is scoped to `api.teselly.com` alone — which is exactly
 *    where `wss://api.teselly.com/cubejs-api/v1` connects.
 *
 * Both halves have to hold. If the API is ever moved to a different registrable
 * domain, `Lax` starts withholding the cookie on this handshake and every widget
 * fails tenant resolution — `queryRewrite` refuses, so it fails CLOSED (an error
 * tile, never another tenant's data), but it fails.
 */
export const createCubeClient = ( config: CubeClientConfig ): CubeApi => {
  const { baseUrl, authToken } = config;

  const transport = new WebSocketTransport({
    apiUrl: getCubeWsUrl( baseUrl ),
    // Placeholder, not a credential — see the docblock. Must be a string.
    authorization: authToken || ''
  });

  // `apiUrl` is required by `CubeApiOptions` even though `transport` supersedes it —
  // the transport owns the socket and never consults this. Passing the REST URL
  // keeps it honest for anything that reads the option for diagnostics.
  return cubejs( async () => authToken || '', { apiUrl: getCubeApiUrl( baseUrl ), transport });
};
